/* LiveStatusBar — single-line "right now" agent status, updating periodically.
   The point isn't a log — it's a continuous heartbeat that says "the agent
   is working on this for you, right now." */

import { useEffect, useState } from 'react';
import type { JobPosting } from '@/modules/job/services/job.api';
import type { TaskItem } from '@/modules/shared/types/yc-demo';

export interface LiveStatusBarProps {
    jobs: JobPosting[];
    tasks: TaskItem[];
    bg?: string;
    text?: string;
    muted?: string;
    accent?: string;
}

function hash(s: string) {
    return s.split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) & 0xffff, 7);
}

interface LiveStep {
    verb: string;
    progress?: { done: number; total: number };
}

function buildSteps(jobs: JobPosting[], tasks: TaskItem[]): LiveStep[] {
    const primary = jobs[0];
    const secondary = jobs[1];
    const cv = tasks.filter((t) => t.kind === 'cv_review').length;
    const seed = primary ? hash(primary._id) : 13;

    const steps: LiveStep[] = [];
    if (primary) {
        steps.push({
            verb: `Screening CVs for ${primary.title}`,
            progress: { done: 9 + (seed % 5), total: 14 + (seed % 6) },
        });
        steps.push({
            verb: `Mapping adjacent companies for ${primary.title}`,
        });
    }
    if (secondary) {
        steps.push({
            verb: `Drafting outreach for ${secondary.title}`,
            progress: { done: 4 + (seed % 3), total: 8 + (hash(secondary._id) % 4) },
        });
    }
    if (cv > 0) {
        steps.push({
            verb: `Drafting your CV review summary`,
        });
    }
    if (steps.length === 0) {
        steps.push({ verb: 'Idle — awaiting your next move' });
    }
    return steps;
}

export function LiveStatusBar({
    jobs,
    tasks,
    bg = 'linear-gradient(135deg, #1f1a14 0%, #2a241e 100%)',
    text = '#fff5ee',
    muted = '#9c948a',
    accent = '#fd775e',
}: LiveStatusBarProps) {
    const steps = buildSteps(jobs, tasks);
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        if (steps.length <= 1) return;
        const id = setInterval(() => setIdx((i) => (i + 1) % steps.length), 4500);
        return () => clearInterval(id);
    }, [steps.length]);

    const step = steps[idx];

    return (
        <div
            style={{
                background: bg,
                color: text,
                padding: '14px 22px',
                borderRadius: 999,
                fontFamily: 'var(--v2-font-sans)',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                boxShadow: '0 6px 20px -8px rgba(0, 0, 0, 0.35)',
            }}
        >
            <span
                style={{
                    fontSize: 9.5,
                    fontWeight: 800,
                    color: muted,
                    letterSpacing: '0.12em',
                    flexShrink: 0,
                }}
            >
                LIVE
            </span>
            <span
                style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: accent,
                    flexShrink: 0,
                    animation: 'liveStatusPulse 2s ease-in-out infinite',
                }}
            />
            <span style={{ fontSize: 11, color: muted, flexShrink: 0, fontWeight: 500 }}>Jacinta</span>
            <span
                key={`${step.verb}-${idx}`}
                style={{
                    fontSize: 13,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: 1,
                    minWidth: 0,
                    animation: 'liveStatusFade 4.5s ease',
                }}
            >
                {step.verb}
                {step.progress && (
                    <span
                        style={{
                            marginLeft: 10,
                            color: muted,
                            fontWeight: 400,
                            fontVariantNumeric: 'tabular-nums',
                            fontSize: 12,
                        }}
                    >
                        {step.progress.done}/{step.progress.total}
                    </span>
                )}
            </span>
            <style>{`
                @keyframes liveStatusPulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(253, 119, 94, 0.6); }
                    50% { box-shadow: 0 0 0 6px rgba(253, 119, 94, 0); }
                }
                @keyframes liveStatusFade {
                    0% { opacity: 0; transform: translateY(3px); }
                    8%, 92% { opacity: 1; transform: translateY(0); }
                    100% { opacity: 0; transform: translateY(-3px); }
                }
            `}</style>
        </div>
    );
}
